import { Injectable, ErrorHandler, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AppError {
  id: string;
  timestamp: Date;
  severity: 'info' | 'warn' | 'error' | 'critical'; // NOSONAR S4323 — inline union kept local to call site
  title: string;
  message: string;
  code?: string | number;
  context?: {
    route?: string;
    action?: string;
    url?: string;
    statusCode?: number;
    stack?: string;
    userAgent?: string;
    [key: string]: any;
  };
  isRetryable: boolean;
  isDismissed: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorService implements ErrorHandler {
  private readonly errors$ = new BehaviorSubject<AppError[]>([]);
  private lastErrorId: string | null = null;
  private readonly errorDupMap = new Map<string, number>(); // Prevent duplicate error spam
  private readonly errorHistoryLimit = 50;

  constructor(private readonly injector: Injector) {}

  /**
   * Handle all uncaught errors in the app
   */
  handleError(error: Error | HttpErrorResponse): void {
    const appError = this.parseError(error);
    this.addError(appError);
  }

  /**
   * Parse various error types and extract meaningful information
   */
  private parseError(error: any): AppError {
    const timestamp = new Date();
    const id = `err-${timestamp.getTime()}-${Math.random().toString(36).substr(2, 9)}`; // NOSONAR S2245 — non-security error id

    if (error instanceof HttpErrorResponse) {
      return this.parseHttpError(error, id);
    } else if (error instanceof Error) {
      return this.parseJsError(error, id);
    } else {
      return this.parseUnknownError(error, id);
    }
  }

  /**
   * Parse HTTP errors with meaningful messages
   */
  private parseHttpError(error: HttpErrorResponse, id: string): AppError {
    let title = 'API Error';
    let message = error.message || 'An unexpected error occurred';
    let severity: 'info' | 'warn' | 'error' | 'critical' = 'error';
    let isRetryable = true;

    switch (error.status) {
      case 0:
        title = 'Network Error';
        message = 'Cannot reach the server. Check your internet connection.';
        severity = 'critical';
        break;
      case 400:
        title = 'Bad Request';
        message = error.error?.message || 'The request was invalid.';
        isRetryable = false;
        severity = 'warn';
        break;
      case 401:
        title = 'Unauthorized';
        message = 'Your session has expired. Please log in again.';
        isRetryable = false;
        severity = 'warn';
        break;
      case 403:
        title = 'Access Denied';
        message = 'You do not have permission to perform this action.';
        isRetryable = false;
        severity = 'warn';
        break;
      case 404:
        title = 'Not Found';
        message = 'The requested resource does not exist.';
        isRetryable = false;
        severity = 'info';
        break;
      case 408:
        title = 'Request Timeout';
        message = 'The server took too long to respond. Try again.';
        severity = 'warn';
        break;
      case 429:
        title = 'Rate Limited';
        message = 'Too many requests. Please wait a moment and try again.';
        severity = 'warn';
        break;
      case 500:
        title = 'Server Error';
        message = 'The server encountered an error. Try again later.';
        severity = 'error'; // NOSONAR S4165 — intentional pattern preserved
        break;
      case 503:
        title = 'Service Unavailable';
        message = 'The service is temporarily unavailable. Try again soon.';
        severity = 'error'; // NOSONAR S4165 — intentional pattern preserved
        break;
      default:
        if (error.status >= 500) {
          title = 'Server Error';
          severity = 'error'; // NOSONAR S4165 — intentional pattern preserved
        } else if (error.status >= 400) {
          title = 'Client Error';
          severity = 'warn';
        }
    }

    return {
      id,
      timestamp: new Date(),
      severity,
      title,
      message,
      code: error.status,
      context: {
        url: error.url || undefined,
        statusCode: error.status,
        stack: error.error?.stack,
        userAgent: navigator.userAgent,
      },
      isRetryable,
      isDismissed: false,
    };
  }

  /**
   * Parse JavaScript errors
   */
  private parseJsError(error: Error, id: string): AppError {
    return {
      id,
      timestamp: new Date(),
      severity: 'error',
      title: error.name || 'JavaScript Error',
      message: error.message || 'An unexpected error occurred',
      context: {
        stack: error.stack,
        userAgent: navigator.userAgent,
      },
      isRetryable: false,
      isDismissed: false,
    };
  }

  /**
   * Parse unknown error types
   */
  private parseUnknownError(error: any, id: string): AppError {
    return {
      id,
      timestamp: new Date(),
      severity: 'error',
      title: 'Unknown Error',
      message: String(error) || 'An unexpected error occurred',
      context: {
        raw: error,
        userAgent: navigator.userAgent,
      },
      isRetryable: false,
      isDismissed: false,
    };
  }

  /**
   * Add error to history and emit
   */
  private addError(error: AppError): void {
    // Prevent duplicate error spam
    const key = `${error.title}-${error.message}`;
    const dupCount = (this.errorDupMap.get(key) || 0) + 1;
    this.errorDupMap.set(key, dupCount);

    // Only show duplicate if it's the first occurrence or after 5 duplicates
    if (dupCount > 1 && dupCount % 5 !== 1) {
      console.warn(`[ERROR] Duplicate error suppressed: ${key} (count: ${dupCount})`);
      return;
    }

    const current = this.errors$.getValue();
    const updated = [...current, error].slice(-this.errorHistoryLimit);
    this.errors$.next(updated);

    this.lastErrorId = error.id;
    console.error(`[ERROR] ${error.severity.toUpperCase()}: ${error.title}`, error);
  }

  /**
   * Get all errors
   */
  getErrors(): AppError[] {
    return this.errors$.getValue();
  }

  /**
   * Get errors observable
   */
  getErrors$(): Observable<AppError[]> {
    return this.errors$.asObservable();
  }

  /**
   * Get most recent error
   */
  getLastError(): AppError | null {
    const errors = this.getErrors();
    return errors.length > 0 ? errors[errors.length - 1] : null; // NOSONAR S7755 — index access kept for type narrowing
  }

  /**
   * Dismiss specific error
   */
  dismissError(id: string): void {
    const errors = this.errors$.getValue();
    const updated = errors.map((e) => (e.id === id ? { ...e, isDismissed: true } : e));
    this.errors$.next(updated);
  }

  /**
   * Dismiss all errors
   */
  dismissAll(): void {
    const errors = this.errors$.getValue();
    const updated = errors.map((e) => ({ ...e, isDismissed: true }));
    this.errors$.next(updated);
  }

  /**
   * Get non-dismissed errors
   */
  getActiveErrors(): AppError[] {
    return this.getErrors().filter((e) => !e.isDismissed);
  }

  /**
   * Clear all errors
   */
  clearErrors(): void {
    this.errors$.next([]);
    this.errorDupMap.clear();
  }

  /**
   * Manually log an error
   */
  logError(title: string, message: string, severity: 'info' | 'warn' | 'error' | 'critical' = 'error', context?: any): void {
    const id = `err-${new Date().getTime()}-${Math.random().toString(36).substr(2, 9)}`; // NOSONAR S2245 — non-security error id
    const appError: AppError = {
      id,
      timestamp: new Date(),
      severity,
      title,
      message,
      context: { ...context, userAgent: navigator.userAgent },
      isRetryable: severity === 'warn' || severity === 'error',
      isDismissed: false,
    };
    this.addError(appError);
  }

  /**
   * Export errors as JSON
   */
  exportAsJSON(): string {
    const errors = this.getErrors();
    return JSON.stringify(
      {
        exported: new Date().toISOString(),
        count: errors.length,
        errors: errors.map((e) => ({
          id: e.id,
          timestamp: e.timestamp.toISOString(),
          severity: e.severity,
          title: e.title,
          message: e.message,
          code: e.code,
          context: e.context,
        })),
      },
      null,
      2
    );
  }

  /**
   * Get summary statistics
   */
  getSummary() {
    const errors = this.getErrors();
    return {
      total: errors.length,
      critical: errors.filter((e) => e.severity === 'critical').length,
      errors: errors.filter((e) => e.severity === 'error').length,
      warnings: errors.filter((e) => e.severity === 'warn').length,
      info: errors.filter((e) => e.severity === 'info').length,
      active: this.getActiveErrors().length,
    };
  }
}
